import { pdfHeaderContext, pdfHeaderText } from "./pdfTagName";
import { loadFuseConsumerIcons, type FuseConsumerIcons } from "./fuseConsumerIcons";
import { protectPdfOutput } from "./protectPdfOutput";
import { drawConnectionRoutePdf } from "./drawConnectionRoutePdf";
import { drawSwitchContact } from "./switchContact";
import { createPlanConnectionRouteStyle, primaryConsumerSpace } from "./connectionRouteScene";
import { groupFuseSymbols, type PositionedFuseNode } from "./fuseSymbolGroups";
import { jsPDF } from "jspdf";
import { loadPdfDocumentHeader } from "./PdfDocumentHeader";
import { registerC1PdfFonts } from "../../ConceptOne/src/lib/C1_PdfFonts";
import type { C1_PdfChapterArtifact } from "../../ConceptOne/src/lib/C1_PdfChapter";
import { buildFuseRouteTree, type FuseReport, type FuseRouteNode, type FuseRouteBranch } from "./fuseReport";

const ink = "#172b3a";
const muted = "#586a75";
type Cursor = { y: number; page: number; nextPage: () => void };

/** Draws vector symbols on a shared baseline; terminal identifiers always stay on one line. */
function drawNode(doc: jsPDF, node: FuseRouteNode, x: number, y: number, width: number): void {
  doc.setFont("Inter", "normal", 400);
  doc.setFontSize(node.kind === "wire" ? 6 : 8);
  doc.setTextColor(ink);
  doc.setDrawColor(ink);
  const center = x + width / 2;
  const label: string[] = node.kind === "terminal" ? [node.label] : doc.splitTextToSize(node.label, width - 8);
  if (node.kind === "supply" || node.kind === "terminalGap") {
    doc.line(x, y, x + width, y);
  } else if (node.kind === "switch") {
    drawSwitchContact({
      line: (x1, y1, x2, y2) => { doc.line(x1, y1, x2, y2); },
      circle: (cx, cy, radius) => { doc.circle(cx, cy, radius); },
      text: (text, tx, ty) => { doc.text(text, tx, ty, { align: "center", lineHeightFactor: 9 / 8 }); },
    }, { left: x, right: x + width, center, y, label, dimmed: node.dimmed });
  } else if (node.kind === "terminal") {
    if (node.label)
      doc.text(label, center, y - 15 - (label.length - 1) * 9, { align: "center", lineHeightFactor: 9 / 8 });
    const terminalWidth = (width - 8) * 0.8;
    const terminalX = center - terminalWidth / 2;
    const number = node.number ?? "?";
    const height = 18;
    doc.rect(terminalX, y - height / 2, terminalWidth, height);
    doc.line(x, y, terminalX, y);
    doc.line(terminalX + terminalWidth, y, x + width, y);
    doc.text(number, center, y, {
      align: "center",
      baseline: "middle",
      lineHeightFactor: 9 / 8,
    });
  } else {
    doc.text(label, center, y, { align: "center", baseline: "middle" });
  }
  doc.setTextColor(ink);
}

/** Measures symbols with the registered font, allowing long labels to wrap. */
function nodeWidth(doc: jsPDF, node: FuseRouteNode): number {
  doc.setFont("Inter", "normal", 400);
  doc.setFontSize(7);
  const nameWidth = node.kind === "consumer" ? doc.getTextWidth([node.label, node.name].filter(Boolean).join(" ")) + 32 : 0;
  doc.setFontSize(node.kind === "wire" ? 6 : 8);
  if (node.kind === "wire") return Math.min(100, Math.max(58, doc.getTextWidth(node.label) + 8));
  // The terminal box uses 80% of the column after its margins. Reserve that
  // actual inner width for the complete identifier; terminal tags never wrap.
  if (node.kind === "terminal") return Math.max(42, doc.getTextWidth(node.label) + 16,
    (doc.getTextWidth(node.number ?? "?") + 6) / 0.8 + 8);
  const textWidth = Math.min(
    node.kind === "consumer" ? 96 : 145,
    Math.max(
      55,
      doc.getTextWidth(node.label) + 16,
      doc.getTextWidth(node.number ?? "") + 18,
      nameWidth,
    ),
  );
  return node.kind === "consumer" ? Math.max(textWidth, doc.getTextWidth(node.label) + 40) : textWidth;
}

/** Limits a cable caption to its own route segment so adjacent cables do not overlap. */
function cableCaptionWidth(nodes: FuseRouteNode[], widths: number[], index: number): number {
  const destinationWidth = nodes[index + 1]?.kind === "consumer" ? widths[index + 1] + 14 : 0;
  return Math.min(120, Math.max(20, widths[index] + destinationWidth - 8));
}

/** Draws a page's symbols after consecutive equipment groups are known. */
function drawPageSymbols(doc: jsPDF, nodes: PositionedFuseNode[], icons: FuseConsumerIcons): void {
  const groups = groupFuseSymbols(nodes);
  const grouped = new Set<PositionedFuseNode>();

  for (const group of groups) {
    for (const item of group.items) grouped.add(item);
    const first = group.items[0];
    const last = group.items[group.items.length - 1];
    doc.setFont("Inter", "normal", 400);
    doc.setFontSize(8);
    doc.setTextColor(ink);
    doc.text(group.title, first.x + first.width / 2, first.y - 30, { align: "center" });
    doc.setDrawColor(muted);
    doc.setLineDashPattern([3, 2], 0);
    doc.rect(first.x + 1, first.y - 25, first.width - 2, last.y - first.y + 45);
    doc.setLineDashPattern([], 0);
  }
  for (const item of nodes) {
    if (["wire", "consumer", "note"].includes(item.node.kind)) continue;
    const node = grouped.has(item) ? { ...item.node, label: item.node.group!.itemLabel } : item.node;
    drawNode(doc, node, item.x, item.y, item.width);
  }
  drawConnectionRoutePdf(doc, nodes, icons);
}

/** Aligns equipment and conductor columns while keeping individual color bubbles compact. */
function measureSymbolColumns(doc: jsPDF, branches: FuseRouteBranch[]): Map<string, number> {
  const widths = new Map<string, number>([
    ["switch", 55],
    ["terminal", 42],
  ]);
  const pending = [...branches];
  const routeStyle = createPlanConnectionRouteStyle((text, size, weight = "normal") => {
    doc.setFont("helvetica", weight);
    doc.setFontSize(size);
    return doc.getTextWidth(text);
  });
  while (pending.length) {
    const branch = pending.pop()!;
    if (branch.node.primaryConsumer) {
      const space = primaryConsumerSpace(branch.node.primaryConsumer.tag, routeStyle);
      widths.set("primaryConsumer", Math.max(widths.get("primaryConsumer") ?? 0, space));
    }
    if (branch.node.kind === "switch" || branch.node.kind === "terminal" || branch.node.kind === "wire") {
      let width = nodeWidth(doc, branch.node);
      if (branch.node.cable) width = Math.max(width, routeStyle.measure(branch.node.cable.label, 6 * routeStyle.scale!) + 14);
      widths.set(branch.node.kind, Math.max(widths.get(branch.node.kind) ?? 0, width));
    }
    pending.push(...branch.children);
  }
  widths.set("supply", widths.get("switch")!);
  widths.set("terminalGap", widths.get("terminal")!);
  return widths;
}

type PotentialLine = { x: number; y?: number; page?: number };
type RouteTask = { branch: FuseRouteBranch; x: number; potential: PotentialLine };
const planContentBottom = 738;

/** Draws a downward continuation arrow with the opposite sheet's chapter/page reference. */
function drawPotentialArrow(
  doc: jsPDF,
  x: number,
  fromY: number,
  tipY: number,
  reference: string,
  right: number,
): void {
  doc.setDrawColor(ink);
  doc.setTextColor(ink);
  doc.setFont("Inter", "normal", 400);
  doc.setFontSize(7);
  doc.line(x, fromY, x, tipY);
  doc.line(x - 3, tipY - 6, x, tipY);
  doc.line(x + 3, tipY - 6, x, tipY);
  const align = x > right - 45 ? "right" : "left";
  doc.text(reference, x + (align === "right" ? -7 : 7), tipY, { align });
}

/** Carries every still-needed potential onto the next sheet, including ancestor branches. */
function continuePotentials(
  doc: jsPDF,
  tasks: RouteTask[],
  cursor: Cursor,
  right: number,
  chapterNumber: number,
): void {
  const active = new Set<PotentialLine>();
  for (const task of tasks) {
    if (task.potential.page === cursor.page && task.potential.y !== undefined) active.add(task.potential);
  }
  const previousPage = cursor.page;
  for (const potential of active)
    drawPotentialArrow(doc, potential.x, potential.y!, 766, `${chapterNumber}.${previousPage + 2}`, right);
  cursor.nextPage();
  for (const potential of active) {
    const tipY = cursor.y + 18;
    drawPotentialArrow(doc, potential.x, cursor.y + 4, tipY, `${chapterNumber}.${previousPage + 1}`, right);
    potential.page = cursor.page;
    potential.y = tipY;
  }
  cursor.y += 22;
}

/** Connects a row to its actual potential; new circuits start below the heading with an open node. */
function connectPotential(
  doc: jsPDF,
  potential: PotentialLine,
  x: number,
  y: number,
  cursor: Cursor,
  right: number,
  chapterNumber: number,
): PotentialLine {
  doc.setDrawColor(ink);
  let incoming = potential;
  if (potential.x !== x - 10 && potential.y !== undefined) {
    // A horizontally wrapped route uses matching references on the same sheet.
    const reference = `${chapterNumber}.${cursor.page + 1}`;
    drawPotentialArrow(doc, potential.x, potential.y, potential.y + 16, reference, right);
    incoming = { x: x - 10, y: y - 8, page: cursor.page };
    drawPotentialArrow(doc, incoming.x, y - 24, incoming.y!, reference, right);
  }
  if (incoming.y === undefined) {
    incoming.y = y - 16;
    incoming.page = cursor.page;
    doc.circle(incoming.x, incoming.y, 2);
    doc.line(incoming.x, incoming.y + 2, incoming.x, y);
  } else doc.line(incoming.x, incoming.y, incoming.x, y);
  doc.line(incoming.x, y, x, y);
  incoming.y = y;
  return incoming;
}

/** Leaves extra clearance when another actor follows a framed actor in the same column. */
function actorBlockGap(previous: PositionedFuseNode[], nodes: FuseRouteNode[], widths: number[], x: number, row: number): number {
  const groups = groupFuseSymbols(previous);
  for (let index = 0; index < nodes.length; index++) {
    const node = nodes[index];
    if (node.kind === "switch" && node.group) {
      for (const group of groups) {
        const last = group.items[group.items.length - 1];
        if (last.node.kind === "switch" && last.x === x && last.row === row - 1 && last.node.group?.id !== node.group.id) return 12;
      }
    }
    x += widths[index] + 10;
  }
  return 0;
}

/** Draws shared route prefixes and keeps their potential lines connected across sheet breaks. */
function drawTree(
  doc: jsPDF,
  branches: FuseRouteBranch[],
  cursor: Cursor,
  right: number,
  chapterNumber: number,
  icons: FuseConsumerIcons,
): void {
  const rootPotential: PotentialLine = { x: 59 };
  const columnWidths = measureSymbolColumns(doc, branches);
  const primaryIds = new Set<string>();
  const pending = [...branches];
  while (pending.length) {
    const branch = pending.pop()!;
    if (branch.node.primaryConsumer) primaryIds.add(branch.node.primaryConsumer.id);
    pending.push(...branch.children);
  }
  let pageSymbols: PositionedFuseNode[] = [];
  let row = 0;
  const tasks: RouteTask[] = [];
  for (let index = branches.length - 1; index >= 0; index--)
    tasks.push({ branch: branches[index], x: 69, potential: rootPotential });
  while (tasks.length) {
    const task = tasks.pop()!;
    let x = task.x;
    let branch: FuseRouteBranch | undefined = task.branch;
    const nodes: FuseRouteNode[] = [];
    const widths: number[] = [];
    const leadingSpaces: number[] = [];
    let endX = x;
    while (branch) {
      const primary = branch.node.primaryConsumer;
      const leadingSpace = primary ? columnWidths.get("primaryConsumer")! : 0;
      const primaryJunction = branch.node.kind === "consumer" && branch.children.length > 0 &&
        primaryIds.has(branch.node.consumerId ?? branch.node.key);
      // The primary tag occupies its own corridor; a through-connection only needs its frame here.
      const symbolWidth = primaryJunction ? 48 : columnWidths.get(branch.node.kind) ?? nodeWidth(doc, branch.node);
      const width = symbolWidth + leadingSpace;
      if (endX + width > right && nodes.length) break;
      if (endX + width > right) {
        x = 69;
        endX = x;
      }
      nodes.push(branch.node);
      widths.push(width);
      leadingSpaces.push(leadingSpace);
      endX += width + 10;
      if (branch.children.length !== 1) break;
      branch = branch.children[0];
    }
    const lastIndex = nodes.length - 1;
    if (nodes[lastIndex]?.kind === "consumer" && !branch?.children.length) {
      const lastX = endX - widths[lastIndex] - 10;
      widths[lastIndex] = Math.max(widths[lastIndex], right - lastX);
    }
    let top = 32;
    let bottom = 12;
    for (let index = 0; index < nodes.length; index++) {
      doc.setFont("Inter", "normal", 400);
      doc.setFontSize(nodes[index].kind === "wire" ? 6 : 8);
      const textWidth = widths[index];
      const lines: string[] = doc.splitTextToSize(nodes[index].label, textWidth - 8);
      top = Math.max(top, lines.length * 10 + 20);
      bottom = Math.max(bottom, lines.length * 5 + 8);
    }
    let captionHeight = 0;
    doc.setFont("Inter", "normal", 400);
    doc.setFontSize(6);
    for (let index = 0; index < nodes.length; index++) {
      const cable = nodes[index].cable;
      if (cable) {
        const lines: string[] = doc.splitTextToSize(cable.label, cableCaptionWidth(nodes, widths, index));
        captionHeight = Math.max(captionHeight, lines.length * 8 + 4);
      }
    }
    // Group captions sit 30 pt above the symbols and need clearance below the circuit heading.
    const groupHeadingTop = nodes.some(node => node.group && (node.kind === "switch" || node.kind === "terminal")) ? 44 : 32;
    if (!pageSymbols.length) top = Math.max(top, groupHeadingTop);
    let blockGap = actorBlockGap(pageSymbols, nodes, widths, x, row);
    let rowHeight = blockGap + top + bottom + 6 + captionHeight;
    if (cursor.y + rowHeight > planContentBottom) {
      drawPageSymbols(doc, pageSymbols, icons);
      pageSymbols = [];
      blockGap = 0;
      continuePotentials(doc, [...tasks, task], cursor, right, chapterNumber);
      top = Math.max(top, groupHeadingTop);
      rowHeight = top + bottom + 6 + captionHeight;
    }
    const y = cursor.y + blockGap + top;
    connectPotential(doc, task.potential, x, y, cursor, right, chapterNumber);
    for (let index = 0; index < nodes.length; index++) {
      const leadingSpace = leadingSpaces[index];
      if (leadingSpace) doc.line(x, y, x + leadingSpace, y);
      pageSymbols.push({
        node: nodes[index],
        x: x + leadingSpace,
        y,
        width: widths[index] - leadingSpace,
        row,
        continues:
          index < nodes.length - 1 ||
          Boolean(branch && (nodes[nodes.length - 1] !== branch.node || branch.children.length)),
        cableLabelY: y + bottom + 4,
        cableLabelWidth: cableCaptionWidth(nodes, widths, index),
      });
      x += widths[index];
      if (index < nodes.length - 1) {
        doc.line(x, y, x + 10, y);
        x += 10;
      }
    }
    cursor.y += rowHeight;
    row++;
    if (branch && nodes[nodes.length - 1] !== branch.node) {
      tasks.push({ branch, x: 69, potential: { x, y, page: cursor.page } });
    } else if (branch?.children.length) {
      const potential: PotentialLine = { x: Math.min(x + 10, right), y, page: cursor.page };
      doc.line(x, y, potential.x, y);
      for (let index = branch.children.length - 1; index >= 0; index--)
        tasks.push({ branch: branch.children[index], x: potential.x + 10, potential });
    }
  }
  drawPageSymbols(doc, pageSymbols, icons);
}

/** Paginates table cells together so no long name or tag is cut off. */
function drawTableRow(
  doc: jsPDF,
  values: string[],
  cursor: Cursor,
  xs: number[],
  widths: number[],
  shaded = false,
): void {
  doc.setFont("Inter", "normal", shaded ? 700 : 400);
  doc.setFontSize(9);
  const cells: string[][] = values.map((value, index) => doc.splitTextToSize(value || "–", widths[index] - 12));
  const count = Math.max(...cells.map((cell) => cell.length));
  let offset = 0;
  while (offset < count) {
    if (cursor.y + 24 > 775) cursor.nextPage();
    const take = Math.min(count - offset, Math.max(1, Math.floor((775 - cursor.y - 10) / 12)));
    const height = take * 12 + 10;
    if (shaded) {
      doc.setFillColor("#edf1f4");
      doc.rect(45, cursor.y, xs[xs.length - 1] + widths[widths.length - 1] - 45, height, "F");
    }
    doc.setTextColor(ink);
    doc.setFont("Inter", "normal", shaded ? 700 : 400);
    doc.setFontSize(9);
    for (let index = 0; index < cells.length; index++) {
      const lines = cells[index].slice(offset, offset + take);
      if (lines.length) {
        const textY = shaded ? cursor.y + height / 2 - (lines.length - 1) * 6 : cursor.y + 6;
        doc.text(lines, xs[index] + 6, textY, { baseline: shaded ? "middle" : "top", lineHeightFactor: 12 / 9 });
      }
    }
    cursor.y += height;
    offset += take;
  }
}

/** Produces both fuse representations as compact vector PDFs using the shared chapter contract. */
export async function createFusePdf(
  report: FuseReport,
  projectName: string,
  mode: "list" | "plan" = "list",
  chapterNumber = 1,
): Promise<C1_PdfChapterArtifact> {
  const plan = mode === "plan";
  const icons = plan ? await loadFuseConsumerIcons(report) : new Map();
  const title = plan ? "Sicherungsplan" : "Sicherungsliste";
  const doc = new jsPDF({ unit: "pt", format: "a4", compress: true });
  await registerC1PdfFonts(doc);
  const header = await loadPdfDocumentHeader();
  const contentRight = doc.internal.pageSize.getWidth() - 45;
  const pages: C1_PdfChapterArtifact["pages"] = [];
  const tocEntries: C1_PdfChapterArtifact["pages"] = [];
  let sectionTitle = "Übersicht";
  let fiTitle = "";
  let cabinetTitle = "";
  let circuitTitle = "";
  const widths = [95, contentRight - 45 - 95 - 100 - 95, 100, 95];
  const xs = [45, 140, contentRight - 195, contentRight - 95];
  const cursor: Cursor = {
    y: 130,
    page: -1,
    nextPage: () => {
      if (pages.length) doc.addPage();
      cursor.page++;
      pages.push({ title: circuitTitle || sectionTitle, sectionTitle, pageIndex: pages.length });
      cursor.y = header.draw(doc, { planType: title, hierarchy: [cabinetTitle, fiTitle] });
      if (!plan) drawTableRow(doc, ["Verbraucher", "Name", "Aktorkanal", "Klemme"], cursor, xs, widths, true);
      if (circuitTitle) drawTableRow(doc, [circuitTitle], cursor, [45], [contentRight - 45], true);
    },
  };
  for (const section of report.sections) {
    sectionTitle = section.title;
    fiTitle = section.fiTitle ?? section.fi;
    cabinetTitle = section.cabinetTitle ?? "";
    circuitTitle = "";
    cursor.nextPage();
    if (!section.circuits.length)
      tocEntries.push({
        title: section.title,
        pageIndex: cursor.page,
        sectionTitle: section.title,
        sectionId: section.id,
        pageLabel: plan ? `${chapterNumber}.${cursor.page + 1}` : undefined,
      });
    for (const circuit of section.circuits) {
      circuitTitle = pdfHeaderText(circuit, { ...pdfHeaderContext(), useTags: true });
      if (cursor.y + 90 > (plan ? planContentBottom : 775)) cursor.nextPage();
      else drawTableRow(doc, [circuitTitle], cursor, [45], [contentRight - 45], true);
      tocEntries.push({
        title: circuitTitle,
        pageIndex: cursor.page,
        sectionTitle: section.title,
        sectionId: section.id,
        pageLabel: plan ? `${chapterNumber}.${cursor.page + 1}` : undefined,
      });
      if (plan) drawTree(doc, buildFuseRouteTree(circuit.consumers), cursor, contentRight, chapterNumber, icons);
      else
        for (const consumer of circuit.consumers)
          drawTableRow(doc, [consumer.tag, consumer.name, consumer.channel, consumer.terminal], cursor, xs, widths);
      if (!circuit.consumers.length)
        drawTableRow(doc, ["Keine Verbraucher zugeordnet."], cursor, [45], [contentRight - 45]);
      cursor.y += 12;
    }
  }
  if (!pages.length) {
    cursor.nextPage();
    drawTableRow(doc, ["Keine Sicherungen vorhanden."], cursor, [45], [contentRight - 45]);
  }
  for (let index = 0; index < pages.length; index++) {
    doc.setPage(index + 1);
    doc.setDrawColor("#aeb9bf");
    doc.line(45, 794, contentRight, 794);
    doc.setTextColor(muted);
    doc.setFont("Inter", "normal", 400);
    doc.setFontSize(6.5);
    doc.text(doc.splitTextToSize(`Projekt: ${projectName}`, 330)[0] ?? "", 45, 808);
    const pageLabel = plan ? `${chapterNumber}.${index + 1}` : `${index + 1} / ${pages.length}`;
    doc.text(pageLabel, contentRight, 808, { align: "right" });
  }
  return { blob: await protectPdfOutput(doc.output("blob"), plan ? "plan" : "list"), pages, tocEntries: tocEntries.length ? tocEntries : pages };
}
